import React, { useEffect, useMemo, useState } from "react";
import { api, codeClasses, PRIORITY_CODES } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClockCounterClockwise, ArrowsHorizontal } from "@phosphor-icons/react";

function diffDiscriminators(prev, next) {
  const prevList = prev?.discriminators || [];
  const nextList = next?.discriminators || [];
  const added = nextList.filter((d) => !prevList.find((p) => p.id === d.id));
  const removed = prevList.filter((d) => !nextList.find((p) => p.id === d.id));
  const modified = nextList
    .map((d) => {
      const p = prevList.find((x) => x.id === d.id);
      if (!p) return null;
      const keys = ["name", "description", "code", "activation_type", "parameter", "operator", "threshold", "threshold_max", "hint", "notes", "active", "order"];
      const changes = keys.filter((k) => JSON.stringify(p[k]) !== JSON.stringify(d[k]));
      if (changes.length === 0) return null;
      return { id: d.id, name: d.name, before: p, after: d, changes };
    })
    .filter(Boolean);
  return { added, removed, modified };
}

export default function VersionHistoryDialog({ open, onOpenChange, protocolId }) {
  const [versions, setVersions] = useState([]);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);

  useEffect(() => {
    if (!open || !protocolId) return;
    (async () => {
      const { data } = await api.get(`/protocols/${protocolId}/versions`);
      const sorted = [...data].sort((a, b) => (a.effective_from || "").localeCompare(b.effective_from || ""));
      setVersions(sorted);
      if (sorted.length >= 2) {
        setLeftId(sorted[sorted.length - 2].protocol_id);
        setRightId(sorted[sorted.length - 1].protocol_id);
      } else if (sorted.length === 1) {
        setLeftId(sorted[0].protocol_id);
        setRightId(sorted[0].protocol_id);
      }
    })();
  }, [open, protocolId]);

  const left = versions.find((v) => v.protocol_id === leftId);
  const right = versions.find((v) => v.protocol_id === rightId);
  const diff = useMemo(() => (left && right ? diffDiscriminators(left, right) : null), [left, right]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={22} /> Storico versioni
          </DialogTitle>
          <DialogDescription>
            {versions.length} versione/i. Ogni triage rimane legato alla versione con cui è stato eseguito.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3">Versione</th>
                  <th className="text-left p-3">Effettiva dal</th>
                  <th className="text-left p-3">Autore</th>
                  <th className="text-left p-3">Discriminatori</th>
                  <th className="text-left p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.protocol_id} className="border-t border-slate-100">
                    <td className="p-3 font-mono text-xs">v.{v.version}</td>
                    <td className="p-3 text-xs text-slate-600">{(v.effective_from || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-xs text-slate-600">{v.author || "—"}</td>
                    <td className="p-3 font-mono text-xs">{(v.discriminators || []).length}</td>
                    <td className="p-3">
                      {v.active
                        ? <span className="inline-flex text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">attiva</span>
                        : <span className="inline-flex text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded bg-slate-200 text-slate-700">superata</span>}
                    </td>
                  </tr>
                ))}
                {versions.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-sm text-slate-500">Nessuna versione</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {versions.length >= 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1">Confronta versione</div>
                  <Select value={leftId || ""} onValueChange={setLeftId}>
                    <SelectTrigger data-testid="diff-left"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {versions.map((v) => <SelectItem key={v.protocol_id} value={v.protocol_id}>v.{v.version} · {(v.effective_from || "").slice(0, 10)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1">con</div>
                  <Select value={rightId || ""} onValueChange={setRightId}>
                    <SelectTrigger data-testid="diff-right"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {versions.map((v) => <SelectItem key={v.protocol_id} value={v.protocol_id}>v.{v.version} · {(v.effective_from || "").slice(0, 10)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {diff && (
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                    <div className="text-xs uppercase tracking-widest text-emerald-700">Aggiunti</div>
                    <div className="font-mono text-2xl" data-testid="diff-added">{diff.added.length}</div>
                  </div>
                  <div className="p-3 rounded-md bg-orange-50 border border-orange-200">
                    <div className="text-xs uppercase tracking-widest text-orange-700">Modificati</div>
                    <div className="font-mono text-2xl" data-testid="diff-modified">{diff.modified.length}</div>
                  </div>
                  <div className="p-3 rounded-md bg-red-50 border border-red-200">
                    <div className="text-xs uppercase tracking-widest text-red-700">Rimossi</div>
                    <div className="font-mono text-2xl" data-testid="diff-removed">{diff.removed.length}</div>
                  </div>
                </div>
              )}

              {diff && (
                <div className="space-y-3">
                  {diff.added.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Aggiunti</div>
                      <ul className="space-y-1 text-sm">
                        {diff.added.map((d) => (
                          <li key={d.id} className="p-2 rounded-md bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                            <span className={`chip-code text-xs ${codeClasses(d.code)}`}>{d.code}</span>
                            <span>{d.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {diff.removed.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-red-700 mb-1">Rimossi</div>
                      <ul className="space-y-1 text-sm">
                        {diff.removed.map((d) => (
                          <li key={d.id} className="p-2 rounded-md bg-red-50 border border-red-200 flex items-center gap-2 line-through">
                            <span className={`chip-code text-xs ${codeClasses(d.code)}`}>{d.code}</span>
                            <span>{d.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {diff.modified.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-orange-700 mb-1">Modificati</div>
                      <ul className="space-y-1 text-sm">
                        {diff.modified.map((m) => (
                          <li key={m.id} className="p-2 rounded-md bg-orange-50 border border-orange-200">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`chip-code text-xs ${codeClasses(m.after.code)}`}>{m.after.code}</span>
                              <span className="font-medium">{m.after.name || m.before.name}</span>
                            </div>
                            <div className="text-xs text-slate-600 flex flex-wrap gap-x-3">
                              {m.changes.map((k) => (
                                <span key={k} className="font-mono">
                                  {k}: <span className="text-red-700">{JSON.stringify(m.before[k])}</span>
                                  <ArrowsHorizontal size={10} className="inline mx-1" />
                                  <span className="text-emerald-700">{JSON.stringify(m.after[k])}</span>
                                </span>
                              ))}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
